import {useCallback, useEffect, useMemo, useState} from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useSelector} from 'react-redux';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import MDInput from 'components/MDInput';
import {ENDPOINT} from 'constants';
import MDButton from 'components/MDButton';
import {
  Card,
  CircularProgress,
  Menu,
  MenuItem,
  Grid,
  Icon,
  Box,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {Loading, Notify} from 'notiflix';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

// Same neutral field styling used by the Orders list toolbar, for a consistent look
const toolbarFieldSx = {
  width: '100%',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    '& fieldset': {borderColor: 'rgba(0,0,0,0.15)'},
    '&:hover fieldset': {borderColor: 'rgba(0,0,0,0.3)'},
    '&.Mui-focused fieldset': {borderColor: '#1A73E8', borderWidth: 1.5},
  },
  '& .MuiInputLabel-root.Mui-focused': {color: '#1A73E8'},
};

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '6%', align: 'left'},
  {Header: 'Client Name', accessor: 'name', width: '25%', align: 'left'},
  {Header: 'Contact No', accessor: 'phoneNumber', width: '18%', align: 'left'},
  {Header: 'Email', accessor: 'email', width: '27%', align: 'left'},
  {Header: 'Country', accessor: 'country', width: '14%', align: 'center'},
  {Header: 'Action', accessor: 'action', width: '10%', align: 'center'},
];

const getRow = (row, idx, openMenu) => ({
  // Kept off the rendered columns — used by the row-level click handler to navigate
  _customer: row,

  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography
      display="block"
      variant="button"
      fontWeight="medium"
      color="secondary"
      title="View Customer Details">
      {row?.firstName} {row?.lastName}
    </MDTypography>
  ),
  phoneNumber: (
    <MDTypography variant="caption">
      {row.phoneNumber
        ? `${row.country?.phoneCode ? `${row.country.phoneCode}-` : ''}${row.phoneNumber}`
        : '—'}
    </MDTypography>
  ),
  email: <MDTypography variant="caption">{row?.email || '—'}</MDTypography>,
  country: (
    <MDTypography variant="caption">
      {row.country?.countryName || '—'}
    </MDTypography>
  ),
  action: (
    <Icon
      sx={{cursor: 'pointer'}}
      fontSize="small"
      onClick={event => {
        event.stopPropagation();
        openMenu(event, row);
      }}>
      more_vert
    </Icon>
  ),
});

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const canRead = usePermission('customers', 'read');
  const canCreate = usePermission('customers', 'create');
  const canUpdate = usePermission('customers', 'update');
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reRender, setReRender] = useState(false);

  // Stable identity (useCallback) — needed so the memoized row-mapping below doesn't get a
  // "changed" dependency on every render just because this closure was recreated.
  const openMenu = useCallback((event, userId) => {
    setSelectedUser(userId);
    setMenu(event.currentTarget);
  }, []);
  const closeMenu = useCallback(() => setMenu(null), []);

  const goToCustomerDetails = rowData => {
    navigate(`/customers/${rowData._customer.id}`);
  };

  const fetchCategories = async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      const {data} = await axios.get(`${ENDPOINT}api/v1/user/admin/users`, {
        headers: {
          Authorization: `${user?.accessToken}`,
        },
      });
      setCustomers(data.data.users);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [reRender, canRead]);

  // Distinct filter options derived from the currently loaded customers
  const countryOptions = useMemo(
    () =>
      Array.from(new Set(customers.map(c => c.country?.countryName).filter(Boolean))).sort(),
    [customers],
  );

  // Search matches name, email and phone number; combined with the Country/Verified filters.
  // Memoized so the array reference is only ever new when the actual result changes — react
  // table's usePagination resets to page 1 whenever the `data` array reference changes
  // (autoResetPage), so an unmemoized `.filter()` here (a new array on every render, even
  // ones unrelated to search/filters) was silently kicking the page back to 1, which is why
  // clicking "2" appeared to do nothing.
  const filteredCustomers = useMemo(
    () =>
      customers.filter(customer => {
        const q = searchQuery.trim().toLowerCase();

        const matchesSearch =
          !q ||
          [
            `${customer.firstName ?? ''} ${customer.lastName ?? ''}`,
            customer.email,
            customer.phoneNumber,
          ]
            .filter(Boolean)
            .some(field => field.toLowerCase().includes(q));

        const matchesCountry = !countryFilter || customer.country?.countryName === countryFilter;

        const matchesVerified =
          !verifiedFilter ||
          (verifiedFilter === 'verified' ? !!customer.isVerified : !customer.isVerified);

        return matchesSearch && matchesCountry && matchesVerified;
      }),
    [customers, searchQuery, countryFilter, verifiedFilter],
  );

  // Also memoized for the same reason as filteredCustomers above — this is the exact array
  // that ends up as DataTable's `table.rows`, so it needs a stable reference too.
  const customerRows = useMemo(
    () => filteredCustomers.map((row, idx) => getRow(row, idx, openMenu)),
    [filteredCustomers, openMenu],
  );

  const hasActiveFilters = Boolean(searchQuery || countryFilter || verifiedFilter);

  const clearFilters = () => {
    setSearchQuery('');
    setCountryFilter('');
    setVerifiedFilter('');
  };

  const handleAddNew = () => {
    navigate('/customers/create');
  };

  const verifyCustomer = async () => {
    try {
      Loading.circle('Updating...');
      await axios.patch(
        `${ENDPOINT}api/v1/user/admin/users/verify/${selectedUser.id}`,
        null,
        {headers: {Authorization: `Bearer ${user?.accessToken}`}},
      );
      Loading.remove();
      Notify.success('Customer verified successfully');
      setReRender(!reRender);
      closeMenu();
    } catch (error) {
      Loading.remove();
      Notify.failure('Error verifying customer');
    }
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Customers. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <MDTypography variant="h6" color="white">
                  Customers List
                </MDTypography>
                <MDBox sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                  <MDTypography variant="button" color="white" sx={{opacity: 0.85}}>
                    {filteredCustomers.length} of {customers.length} customers
                  </MDTypography>
                  {canCreate && (
                    <MDButton variant="contained" onClick={handleAddNew}>
                      Add New Customer
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>

              <MDBox
                mx={2}
                mt={2}
                px={2.5}
                py={2.5}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                <MDInput
                  fullWidth
                  label="Search by name, email or phone"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  sx={{...toolbarFieldSx, mb: 2.5}}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{color: 'rgba(0,0,0,0.45)'}} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" sx={toolbarFieldSx}>
                      <InputLabel>Country</InputLabel>
                      <Select
                        label="Country"
                        value={countryFilter}
                        onChange={e => setCountryFilter(e.target.value)}>
                        <MenuItem value="">All Countries</MenuItem>
                        {countryOptions.map(country => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" sx={toolbarFieldSx}>
                      <InputLabel>Verification Status</InputLabel>
                      <Select
                        label="Verification Status"
                        value={verifiedFilter}
                        onChange={e => setVerifiedFilter(e.target.value)}>
                        <MenuItem value="">All Customers</MenuItem>
                        <MenuItem value="verified">Verified</MenuItem>
                        <MenuItem value="unverified">Not Verified</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {hasActiveFilters && (
                  <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                    <MDBox
                      onClick={clearFilters}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        color: '#1A73E8',
                        '&:hover': {opacity: 0.75},
                      }}>
                      <ClearIcon fontSize="small" />
                      <MDTypography variant="button" fontWeight="medium" sx={{color: 'inherit'}}>
                        Clear filters
                      </MDTypography>
                    </MDBox>
                  </Box>
                )}
              </MDBox>

              {loading ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  mb={6}
                  pt={3}>
                  <CircularProgress size="3rem" color="inherit" />
                </Grid>
              ) : (
                <MDBox pt={3}>
                  <DataTable
                    table={{
                      columns,
                      rows: customerRows,
                    }}
                    isSorted={false}
                    entriesPerPage={{defaultValue: 10, entries: [10, 25, 50]}}
                    showTotalEntries={true}
                    noEndBorder
                    hoverEffect
                    onRowClick={goToCustomerDetails}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Menu
        id="simple-menu"
        anchorEl={menu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(menu)}
        onClose={closeMenu}>
        <MenuItem
          disabled={!canUpdate || Boolean(selectedUser?.isVerified)}
          onClick={verifyCustomer}>
          Verify Customer
        </MenuItem>
      </Menu>
    </DashboardLayout>
  );
};

export default Customers;
